import React, { useCallback, useState } from 'react';
import { ExternalActivityListener } from '../external-activity-listener';
import { StudioButton } from './StudioButton';
import type { ButtonSize } from './StudioButton';
import { Icon } from '../icon/Icon';
import { cn } from '@/lib/utils';

export interface StudioMenuOption<T> {
  value: T;
  label?: string;
  description?: string;
  disabled?: boolean;
}

export interface StudioMenuProps<T> {
  options: StudioMenuOption<T>[];
  renderItem?: (t: StudioMenuOption<T>) => JSX.Element;
  onSelect: (t: T) => void;
  size: ButtonSize;
  testId?: string;
  label: string;
  buttonContent?: React.ReactNode;
  itemClassName?: string;
}

export const StudioMenu = <T,>({
  options,
  renderItem,
  size,
  onSelect,
  testId = 'studio-menu',
  label,
  buttonContent,
  itemClassName,
}: StudioMenuProps<T>) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen(!open), [open, setOpen]);
  const onClick = useCallback(
    (value: T) => {
      onSelect(value);
      setOpen(false);
    },
    [onSelect, setOpen],
  );
  return (
    <ExternalActivityListener onExternalActivity={() => setOpen(false)} testId={testId}>
      <StudioButton
        size={size}
        type="button"
        onClick={toggleOpen}
        aria-haspopup
        aria-expanded={open}
        aria-label={label}
      >
        {buttonContent ?? <Icon type="ellipsis-h" />}
      </StudioButton>
      {open && (
        <div
          role="menu"
          data-testid="studio-menu-content"
          className={cn(
            'absolute bg-venn-white z-modal flex flex-col right-0 py-[7px] px-0',
            'border border-solid border-venn-grey-40 shadow-[0_6px_6px_0_inherit] shadow-venn-black/20',
          )}
        >
          {options.map((option) => (
            <button
              disabled={option.disabled}
              type="button"
              key={option.label}
              role="menuitem"
              onClick={() => onClick(option.value)}
              className={cn('p-2.5 text-right text-venn-black hover:bg-venn-grey-20 hover:text-[unset]', itemClassName)}
            >
              {/*
               * button does not accept a string in the children prop
               * so we need to wrap the string in a fragment
               */}
              {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
              {renderItem ? renderItem(option) : <>{option.label ?? option.value}</>}
            </button>
          ))}
        </div>
      )}
    </ExternalActivityListener>
  );
};
